import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../../styles/variablesCss';
import { useCheckoutContext } from '../../../context/CheckoutContext';
import { useCart } from '../../../context/CartContext';
import { MONTO_MINIMO_ENVIO_GRATIS } from '../../../Config/Const';

const PagoTarjetaDebito = ({ total: totalProp }) => {
  // Estado local para la tarjeta seleccionada
  const [tarjetaSeleccionada, setTarjetaSeleccionada] = useState('');
  
  // Context para actualizar los valores globales
  const { setTarjeta, setCuotas, setCoeficiente, setTotalDeCuota, setTotalFinal, setMarcaTarjeta, 
    tipoEntrega, precioFleteDireccion, deseaFactura } = useCheckoutContext();
  const { totales, calcularTotal } = useCart();

  // Calcular el total dinámicamente incluyendo percepciones si corresponde
  const subtotal = totales.subtotal || calcularTotal();
  const descuentoTotal = totales.descuentos || 0;
  const percepciones = totales.percepciones || 0;
  
  // Agregar percepciones solo si el usuario desea factura A o B
  const percepcionesAplicadas = deseaFactura ? percepciones : 0;
  const totalProductos = subtotal - descuentoTotal + percepcionesAplicadas;
  
  // Calcular el flete final
  let fleteAMostrar = 0;
  if (totalProductos > MONTO_MINIMO_ENVIO_GRATIS) {
    fleteAMostrar = 0;
  } else {
    const fleteCarrito = totales.precioFlete || 0;
    const fleteDireccion = precioFleteDireccion || 0;
    fleteAMostrar = parseInt(tipoEntrega) === 2 
      ? Math.max(fleteCarrito, fleteDireccion) 
      : fleteCarrito;
  }
  
  // Total final incluyendo el flete (base local)
  const totalLocal = totalProductos + fleteAMostrar;
  // Preferir total proveniente de props (con cupón/flete) si existe
  const total = (typeof totalProp === 'number' && !Number.isNaN(totalProp)) ? totalProp : totalLocal;

  // Opciones de tarjetas de débito disponibles
  const opcionesTarjeta = [
    { id: '1', nombre: 'VISA DEBITO', marcaTarjeta: 'V' },
    { id: '2', nombre: 'MAESTRO', marcaTarjeta: 'MA' },
    { id: '3', nombre: 'CABAL DEBITO', marcaTarjeta: 'CABAL_ARGENTINA' },
    { id: '4', nombre: 'MASTERCARD DEBITO', marcaTarjeta: 'M' }
  ];

  // Función para seleccionar una tarjeta
  const seleccionarTarjetaDebito = (tarjeta) => {
    setTarjetaSeleccionada(tarjeta);
    
    // Para tarjetas de débito, siempre es una cuota y coeficiente 1
    setTarjeta(tarjeta.nombre);
    setMarcaTarjeta(tarjeta.marcaTarjeta);
    setCuotas(1); 
    setCoeficiente(1);
    setTotalDeCuota(total.toFixed(2));
    setTotalFinal(total.toFixed(2));
  };

  // Efecto para recalcular cuando cambia el total (por factura/percepciones)
  useEffect(() => {
    if (tarjetaSeleccionada) {
      setTotalDeCuota(total.toFixed(2));
      setTotalFinal(total.toFixed(2));
    }
  }, [total, deseaFactura, percepciones, tarjetaSeleccionada]);

  return (
    <View style={styles.container}>
      <Text style={styles.titulo}>Selecciona tu tarjeta de débito</Text>
      
      {/* Selector de tarjeta para Web */}
      {Platform.OS === 'web' ? (
        <View style={styles.selectorContainer}>
          <select 
            style={styles.selector}
            value={tarjetaSeleccionada.id || ''}
            onChange={(e) => {
              const tarjeta = opcionesTarjeta.find(t => t.id === e.target.value);
              if (tarjeta) {
                seleccionarTarjetaDebito(tarjeta);
              }
            }}
          >
            <option value="">Selecciona una tarjeta</option>
            {opcionesTarjeta.map((tarjeta) => (
              <option key={tarjeta.id} value={tarjeta.id}>
                {tarjeta.nombre}
              </option>
            ))}
          </select>
          <MaterialCommunityIcons 
            name="chevron-down" 
            size={20} 
            color={colores.textoPrincipal}
            style={styles.selectIcon}
          />
        </View>
      ) : (
        // Para versión móvil
        <View style={styles.tarjetasContainer}>
          {opcionesTarjeta.map((tarjeta) => (
            <TouchableOpacity
              key={tarjeta.id}
              style={[
                styles.tarjetaItem,
                tarjetaSeleccionada.id === tarjeta.id && styles.tarjetaSeleccionada
              ]}
              onPress={() => seleccionarTarjetaDebito(tarjeta)}
            >
              <Text style={[
                styles.tarjetaNombre,
                tarjetaSeleccionada.id === tarjeta.id && styles.tarjetaTextoSeleccionado
              ]}>
                {tarjeta.nombre}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 15,
  },
  titulo: {
    fontSize: 16,
    fontFamily: fuentes.semiBold,
    marginBottom: 15,
    color: colores.textoPrincipal,
  },
  selectorContainer: {
    position: 'relative',
    marginBottom: 20,
  },
  selector: {
    width: '100%',
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colores.borde,
    backgroundColor: colores.blanco,
    fontSize: 16,
    color: colores.textoPrincipal,
    appearance: 'none',
  },
  selectIcon: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{translateY: -10}],
    pointerEvents: 'none',
  },
  tarjetasContainer: {
    marginVertical: 10,
  },
  tarjetaItem: {
    padding: 15,
    marginBottom: 10,
    borderRadius: 6,
    backgroundColor: colores.fondoClaro,
    borderWidth: 1,
    borderColor: colores.borde,
  },
  tarjetaSeleccionada: {
    backgroundColor: colores.rojo,
    borderColor: colores.rojo,
  },
  tarjetaNombre: {
    fontSize: 16,
    color: colores.textoPrincipal,
  },
  tarjetaTextoSeleccionado: {
    color: colores.blanco,
    fontWeight: 'bold',
  },
  infoContainer: {
    marginTop: 20,
    padding: 15,
    backgroundColor: colores.fondoClaro,
    borderRadius: 6,
  },
  infoTexto: {
    fontSize: 14,
    color: colores.textoPrincipal,
    marginBottom: 10,
  },
  resumenContainer: {
    marginTop: 15,
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: colores.borde,
  },
  resumenTotal: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
    textAlign: 'right',
  }
});

export default PagoTarjetaDebito;
